import { useEffect, useState, type FormEvent } from 'react';
import type { Campaña, Miembro } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { aporteCampanaService } from '../services/aporteCampanaService';
import { campanaService } from '../services/campanaService';
import { puedeAdministrarConvocatoriaLocal } from '../utils/permissions';
import { Button } from './ui/Button';
import { Combobox } from './ui/Combobox';
import { Select } from './ui/Select';
import { DialogShell } from './ui/DialogShell';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { FormErrorAlert } from './ui/FormErrorAlert';
interface Props {
  readonly isOpen: boolean; readonly onClose: () => void; readonly onSuccess: () => void;
  readonly campañaId: string; readonly miembros: readonly Miembro[];
}
export function CompromisoModal(props: Props) {
  if (!props.isOpen) return null;
  return <FormularioCompromiso key={props.campañaId} {...props} />;
}
function FormularioCompromiso({ onClose, onSuccess, campañaId, miembros }: Omit<Props, 'isOpen'>) {
  const { usuario } = useAuth();
  const [campana, setCampana] = useState<Campaña | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [origen, setOrigen] = useState('miembro');
  const [miembroId, setMiembroId] = useState('');
  const [nombreExterno, setNombreExterno] = useState('');
  const [monto, setMonto] = useState('');
  const [tipo, setTipo] = useState<'dinero' | 'objeto'>('dinero');
  const [descripcion, setDescripcion] = useState('');
  useEffect(() => {
    let vigente = true;
    campanaService.getById(campañaId).then(c => { if (vigente) setCampana(c); })
      .catch(() => { if (vigente) setError('No pudimos verificar la convocatoria.'); });
    return () => { vigente = false; };
  }, [campañaId]);
  const autorizada = (c: Campaña | null) => puedeAdministrarConvocatoriaLocal(usuario, c);
  const puedeGuardar = autorizada(campana);
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    const valor = Number(monto);
    if (!puedeGuardar || !Number.isFinite(valor) || valor <= 0 || (origen === 'miembro' && !miembroId)
      || (origen === 'externo' && !nombreExterno.trim()) || (tipo === 'objeto' && !descripcion.trim())) {
      setError('Completa los datos de un compromiso para una convocatoria abierta de tu Lumisial.'); return;
    }
    setSubmitting(true); setError(null);
    try {
      if (!autorizada(await campanaService.getById(campañaId))) throw new Error('La convocatoria cambió.');
      await aporteCampanaService.createCompromiso({ campaña_id: campañaId,
        miembro_id: origen === 'miembro' ? miembroId : undefined,
        nombre_externo: origen === 'externo' ? nombreExterno.trim() : undefined,
        monto_comprometido: valor, monto_aportado: 0, tipo_aporte: tipo,
        descripcion_objeto: tipo === 'objeto' ? descripcion.trim() : undefined });
    } catch { setError('No pudimos registrar el compromiso. Revisa el estado de la convocatoria y tus datos.'); setSubmitting(false); return; }
    setSubmitting(false); onClose(); onSuccess();
  };
  return <DialogShell isOpen onClose={onClose} loading={submitting} title="Registrar compromiso">
    <form onSubmit={guardar} className="space-y-4">
      <FormErrorAlert error={error} />
      {!puedeGuardar && <p>Los nuevos compromisos requieren una convocatoria abierta y la junta responsable del Lumisial. Finalizada conserva la contabilidad de compromisos anteriores.</p>}
      <fieldset disabled={submitting || !puedeGuardar} className="space-y-4">
        <Select label="Tipo de aportante" value={origen} onChange={setOrigen}
          options={[{ value: 'miembro', label: 'Miembro' }, { value: 'externo', label: 'Externo' }]} />
        {origen === 'miembro' ? <Combobox label="Miembro" value={miembroId} onChange={setMiembroId}
          options={miembros.map(m => ({ value: m.id, label: `${m.nombres} ${m.apellidos}`, subtitle: m.rut_pasaporte }))} required />
          : <TextInput id="compromiso-externo" label="Nombre del aportante" value={nombreExterno} onChange={setNombreExterno} required />}
        <TextInput id="compromiso-monto" label="Monto comprometido o valor estimado" type="number"
          value={monto} onChange={setMonto} min={0.01} step={0.01} required />
        <Select label="Tipo de aporte" value={tipo} onChange={v => setTipo(v === 'objeto' ? 'objeto' : 'dinero')}
          options={[{ value: 'dinero', label: 'Dinero' }, { value: 'objeto', label: 'Objeto' }]} />
        {tipo === 'objeto' && <Textarea id="compromiso-objeto" label="Descripción del objeto" value={descripcion}
          onChange={e => setDescripcion(e.target.value)} required />}
      </fieldset>
      <div className="flex justify-end gap-3"><Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button type="submit" isLoading={submitting} disabled={!puedeGuardar}>Registrar compromiso</Button></div>
    </form>
  </DialogShell>;
}
